/**
 * BehavioralCampaignBuilder — Admin → Strategy
 * ------------------------------------------------------------------
 * Tactical operator panel that auto-assembles the next behavioral
 * campaign by chaining intelligence-vetted assets into a 5-stage
 * narrative arc, then commits it into the operating system.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Network, Zap, GitBranch, AlertTriangle, Workflow } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  proposeCampaign,
  commitCampaign,
  ROLE_LABEL,
  type CampaignProposal,
  type StrategicRole,
} from "@/lib/campaignEngine";

const roleColor: Record<StrategicRole, string> = {
  hook_ambiguity: "text-cyan border-cyan/40 bg-cyan/10",
  behavioral_reveal: "text-amber-300 border-amber-400/40 bg-amber-500/10",
  escalation_confrontation: "text-red-400 border-red-500/40 bg-red-500/10",
  pov_reframing: "text-violet-300 border-violet-400/40 bg-violet-500/10",
  replay_reinforcement: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
};

export default function BehavioralCampaignBuilder() {
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [proposal, setProposal] = useState<CampaignProposal | null>(null);
  const [scanLog, setScanLog] = useState<string[]>([]);

  const pulse = (line: string) =>
    setScanLog((l) => [`⟢ ${new Date().toLocaleTimeString()} · ${line}`, ...l].slice(0, 6));

  const propose = async () => {
    setLoading(true);
    setProposal(null);
    pulse("Sequencing behavioral assets...");
    try {
      const p = await proposeCampaign();
      setProposal(p);
      pulse(`Chain assembled · ${p.sequences.length} stages · pivot at #3`);
      pulse(`Projected virality ${p.projected_virality} · retention ${p.projected_retention}`);
    } catch (e: any) {
      toast({ title: "Sequencing failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    propose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deploy = async () => {
    if (!proposal) return;
    setCommitting(true);
    pulse("Deploying campaign to operating system...");
    try {
      await commitCampaign(proposal);
      pulse("Campaign live · biases reinforced · prediction memory updated");
      toast({ title: "Campaign deployed", description: proposal.campaign_name });
      await propose();
    } catch (e: any) {
      toast({ title: "Deploy failed", description: e.message, variant: "destructive" });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan/60 to-transparent animate-pulse" />
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2 tracking-wide">
            <Workflow className="w-5 h-5 text-cyan" />
            Behavioral Campaign Builder
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={propose} disabled={loading}
              className="h-8 border-cyan/40 text-cyan hover:bg-cyan/10">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3 mr-1" />}
              Re-sequence
            </Button>
            <Button size="sm" onClick={deploy} disabled={!proposal || committing}
              className="h-8 bg-cyan/20 text-cyan border border-cyan/50 hover:bg-cyan/30">
              {committing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
              Deploy Campaign
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tactical terminal */}
        <div className="rounded border border-glass-border bg-charcoal-deep/80 p-2 font-mono text-[10px] text-cyan/80 min-h-[68px] space-y-0.5">
          {scanLog.length === 0 ? <div className="opacity-50">awaiting signal...</div> :
            scanLog.map((l, i) => <div key={i} className={i === 0 ? "text-cyan" : "opacity-60"}>{l}</div>)}
        </div>

        {proposal && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <Stat label="Projected Virality" value={proposal.projected_virality} suffix="%" tone="cyan" />
              <Stat label="Projected Retention" value={proposal.projected_retention} suffix="%" tone="emerald" />
              <Stat label="Conditioning Score" value={proposal.audience_conditioning_score} suffix="%" tone="violet" />
              <Stat label="Fatigue Risk" value={proposal.fatigue_risk} suffix="%"
                tone={proposal.fatigue_risk > 60 ? "red" : "amber"} />
            </div>

            <div className="text-[11px] text-muted-foreground italic px-1">
              {proposal.rationale}
            </div>

            {/* Escalation chain */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <Network className="w-3 h-3" /> Narrative Chain
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {proposal.sequences.map((s, idx) => (
                  <div key={s.asset_id} className="flex items-center gap-1.5">
                    <div className={`px-2 py-1 rounded border text-[10px] font-mono ${roleColor[s.strategic_role]}`}>
                      <div className="opacity-70">#{s.sequence_order}</div>
                      <div>{ROLE_LABEL[s.strategic_role]}</div>
                      <div className="opacity-60 text-[9px] mt-0.5">
                        replay {s.projected_replay_impact}% · ret {s.projected_retention_impact}%
                      </div>
                    </div>
                    {idx < proposal.sequences.length - 1 && (
                      <span className="text-cyan/40 text-xs">→</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {proposal.fatigue_risk > 60 && (
              <div className="flex items-center gap-2 text-[11px] text-amber-300 border border-amber-400/30 bg-amber-500/5 rounded px-2 py-1.5">
                <AlertTriangle className="w-3 h-3" />
                Audience fatigue risk elevated — consider injecting reinforcement pivot.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, suffix, tone }: { label: string; value: number; suffix?: string; tone: "cyan" | "emerald" | "violet" | "amber" | "red" }) {
  const toneCls = {
    cyan: "text-cyan border-cyan/30",
    emerald: "text-emerald-300 border-emerald-400/30",
    violet: "text-violet-300 border-violet-400/30",
    amber: "text-amber-300 border-amber-400/30",
    red: "text-red-400 border-red-500/30",
  }[tone];
  return (
    <div className={`rounded border bg-charcoal-deep/60 p-2 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-lg leading-tight">{value}{suffix}</div>
      <Progress value={value} className="h-0.5 mt-1" />
    </div>
  );
}
